import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { LOGO } from '../assets';

// Shield logo mark
export function Logo({ size = 40, glow = true, className = '' }) {
  return (
    <img src={LOGO} alt="SPC" width={size} height={size}
      className={`spc-logo ${glow ? 'spc-logo-glow' : ''} ${className}`}
      style={{ width: size, height: size }} />
  );
}

// Scroll / mount reveal
export function Reveal({ children, delay = 0, y = 22, className = '', as = 'div' }) {
  const M = motion[as] || motion.div;
  return (
    <M
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >{children}</M>
  );
}

// Stagger container + item
export const Stagger = ({ children, className = '', style, ...rest }) => (
  <motion.div className={className} style={style} {...rest}
    initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.1 }}
    variants={{ hidden: {}, show: { transition: { staggerChildren: 0.07 } } }}>
    {children}
  </motion.div>
);
export const Item = ({ children, className = '', style, ...rest }) => (
  <motion.div className={className} style={style} {...rest}
    variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } } }}>
    {children}
  </motion.div>
);

// Self-animating item (animates on mount regardless of parent) — use for dynamic lists
export const Pop = ({ children, className = '', style, delay = 0, ...rest }) => (
  <motion.div className={className} style={style} {...rest}
    initial={{ opacity: 0, y: 16, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ duration: 0.4, delay, ease: [0.22, 1, 0.36, 1] }}>
    {children}
  </motion.div>
);

// Animated count-up number
export function CountUp({ value = 0, duration = 1200, className = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let raf; const start = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(eased * value));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, duration]);
  return <span ref={ref} className={className}>{n.toLocaleString()}</span>;
}

// Skeleton loader
export function Skeleton({ w = '100%', h = 16, r = 8, className = '', style = {} }) {
  return <span className={`skel ${className}`} style={{ width: w, height: h, borderRadius: r, ...style }} />;
}

// Signature SPC logo loader — the real logo, gently rotating + pulsing
export function SpcLoader({ size = 32, label, className = '', flash = false }) {
  return (
    <span
      role="status"
      aria-label={label || 'Loading'}
      className={`${flash ? 'spc-loader-flash' : 'spc-loader'} ${className}`}
      style={{ width: size, height: size }}
    >
      <img src={LOGO} alt="" width={size} height={size} />
    </span>
  );
}

// Cycles through a list of messages every 2s — used on the boot screen while data loads
export function CyclingText({ messages, className = '' }) {
  const [i, setI] = React.useState(0);
  React.useEffect(() => {
    const t = setInterval(() => setI((n) => (n + 1) % messages.length), 2000);
    return () => clearInterval(t);
  }, [messages.length]);
  return <span className={className} data-testid="boot-cycling-text">{messages[i]}</span>;
}
export function SpcLoaderBlock({ label = 'Loading the Clubhouse…' }) {
  return (
    <div className="spc-loader-full">
      <SpcLoader size={44} />
      <small>{label}</small>
    </div>
  );
}

// Live activity marquee ticker
export function Marquee({ items = [] }) {
  if (!items.length) return null;
  const loop = [...items, ...items];
  return (
    <div className="marquee" data-testid="live-marquee" aria-label="Live club activity">
      <div className="marquee-track">
        <span className="marquee-item">
          <span className="live-tag"><span className="live-pulse" />LIVE</span>
        </span>
        {loop.map((t, i) => (
          <span className="marquee-item" key={i}>
            <span className="marquee-dot" />
            <span>{t}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

// Illustrated empty state
export function EmptyState({ icon: Icon, title, message, action, testId }) {
  return (
    <div className="empty-state" data-testid={testId}>
      {Icon && <div className="empty-state-icon"><Icon size={30} strokeWidth={1.6} /></div>}
      <h4>{title}</h4>
      {message && <p>{message}</p>}
      {action}
    </div>
  );
}

// Circular progress ring
export function Ring({ value = 0, size = 92, stroke = 9, label, sub }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, value));
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  return (
    <div className="ring" ref={ref} style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#23262a" strokeWidth={stroke} fill="none" />
        <motion.circle cx={size / 2} cy={size / 2} r={r} stroke="var(--spc-yellow)" strokeWidth={stroke}
          fill="none" strokeLinecap="round" strokeDasharray={c}
          initial={{ strokeDashoffset: c }}
          animate={{ strokeDashoffset: inView ? c - (c * pct) / 100 : c }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ filter: 'drop-shadow(0 0 6px rgba(225,226,10,.5))' }} />
      </svg>
      <div className="ring-center">
        <strong>{label}</strong>
        {sub && <span>{sub}</span>}
      </div>
    </div>
  );
}

export { motion };
